import { useSetAtom } from "jotai";
import { useCallback } from "react";

import { EMPTY_ONBOARDING_ANSWERS, type OnboardingAnswers } from "../types";
import {
  onboardingV2AnswersAtom,
  onboardingV2BusinessClaimDraftAtom,
  onboardingV2CompletedAtom,
  onboardingV2IntroStageAtom,
  onboardingV2IntroTourPageAtom,
  onboardingV2MinimisedPreferenceAtom,
  onboardingV2ModalRequestedAtom,
  onboardingV2PendingNewUserAtom,
  onboardingV2ReplayAtom,
  onboardingV2StepAtom,
} from "./atoms";

/**
 * Whether the answers in this browser are a real run rather than the blank
 * placeholder.
 *
 * Backs the carry-over rule at sign-up - @see `useMarkNewUserForOnboarding`.
 * Any one of the steps having been answered is enough: someone who quit after
 * step 1 still told us something, and asking them again for it is the thing
 * carry-over exists to avoid.
 */
export function hasOnboardingAnswers(answers: OnboardingAnswers) {
  return (
    !!answers.userType ||
    !!answers.audience ||
    answers.accessNeedsAnswered ||
    answers.accessNeeds.length > 0 ||
    !!answers.city
  );
}

/**
 * Clears what the departing user told onboarding, leaving the browser's own
 * record of having shown the flow.
 *
 * The split matters. The answers are personal - access needs, who they are
 * answering for, which city - and go when the session does, so the next person
 * to sign in on a shared machine does not inherit them. The completion flag is
 * a fact about this browser ("the flow has run here"), and clearing it would put
 * every returning user who simply logged out back through onboarding on their
 * next visit, which is exactly what the new-account path exists to handle
 * properly instead.
 *
 * The pending debt goes too: it belongs to the account that signed up, and that
 * account has left.
 *
 * Deliberately does **not** touch `manualLocationAtom`. Where the browser is
 * pointed is a preference of this browser, the same as one set from the header,
 * and native leaves its equivalent alone for the same reason.
 */
export function useClearOnboardingForSession() {
  const setAnswers = useSetAtom(onboardingV2AnswersAtom);
  const setStep = useSetAtom(onboardingV2StepAtom);
  const setReplay = useSetAtom(onboardingV2ReplayAtom);
  const setPendingNewUser = useSetAtom(onboardingV2PendingNewUserAtom);
  const setModalRequested = useSetAtom(onboardingV2ModalRequestedAtom);
  const setMinimisedPreference = useSetAtom(
    onboardingV2MinimisedPreferenceAtom,
  );
  const setBusinessClaimDraft = useSetAtom(onboardingV2BusinessClaimDraftAtom);

  return useCallback(() => {
    setAnswers(EMPTY_ONBOARDING_ANSWERS);
    setBusinessClaimDraft(null);
    setStep("USER_TYPE");
    setReplay(false);
    setPendingNewUser(false);
    setModalRequested(false);
    setMinimisedPreference(null);
  }, [
    setAnswers,
    setBusinessClaimDraft,
    setMinimisedPreference,
    setModalRequested,
    setPendingNewUser,
    setReplay,
    setStep,
  ]);
}

/**
 * Puts the flow back to how a first-time visitor finds it: the intro owed
 * again, answers cleared, position back at step 1, completion flag off.
 *
 * The single definition of a full reset — `useOnboardingFlow`'s
 * `resetOnboarding` is this hook. Split out because the developer-only
 * "Reset onboarding" row in the profile menu needs it too, and
 * `useOnboardingFlow` is the wrong thing to mount from a menu: it builds the
 * whole step machine and subscribes to every answer, none of which a settings
 * row has any business doing. Native splits it for the same reason — @see
 * `apps/native/src/onboarding/state/useResetOnboarding.ts`.
 *
 * Clearing the completion flag is what makes the flow replay, and on desktop it
 * replays **immediately** rather than on the next launch:
 * `onboardingV2PresentationAtom` derives from that flag, so the docked sheet is
 * back on screen as soon as this returns - collapsed to its pill on a session
 * that landed on an excluded route (a venue deep link, a password reset).
 *
 * Deliberately limited to the flow's own state. The city onboarding applied to
 * the home page is left alone — it is the page's location now, the same as one
 * picked from the location switcher, and the replayed flow overwrites it when
 * the user picks again.
 */
export function useResetOnboarding() {
  const clearForSession = useClearOnboardingForSession();
  const setCompleted = useSetAtom(onboardingV2CompletedAtom);
  const setIntroStage = useSetAtom(onboardingV2IntroStageAtom);
  const setIntroTourPage = useSetAtom(onboardingV2IntroTourPageAtom);

  return useCallback(() => {
    clearForSession();
    setCompleted(false);
    setIntroStage("welcome");
    setIntroTourPage(null);
  }, [clearForSession, setCompleted, setIntroStage, setIntroTourPage]);
}
